<template>
  <div class="dashboard-container">
    <el-header class="header">
      <div class="header-content">
        <div class="header-left">
          <div class="logo">
            <i class="el-icon-car logo-icon"><img width="30px" src="../assets/img.png"></i>
            <h2 class="logo-text">驾校学员预约系统</h2>
          </div>
          <el-button
              class="mobile-menu-btn"
              @click="showMobileMenu = !showMobileMenu"
              icon="el-icon-menu"
              v-if="isMobile"
          ></el-button>
        </div>
        <div class="header-right">
          <el-button
              class="global-bg-control-btn"
              @click="toggleGlobalBackgroundSettings"
              icon="el-icon-picture-outline"
              :type="isGlobalBgActive ? 'primary' : 'default'"
              circle
              size="small"
              :title="isGlobalBgActive ? '修改全局背景' : '设置全局背景'"
          ></el-button>
          <div class="user-area">
            <i class="el-icon-user user-icon"></i>
            <div class="user-info">
              <span class="user-name" v-if="userInfo.realName">
                {{ userInfo.realName }}
              </span>
              <span class="user-name" v-else>加载中...</span>
              <el-tag
                  :type="getRoleTagType(userRole)"
                  size="mini"
                  class="role-tag"
                  effect="dark"
              >
                {{ userRoleText }}
              </el-tag>
            </div>
            <el-dropdown trigger="click" class="user-dropdown">
              <span class="dropdown-trigger">
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item @click.native="handleProfile">
                  <i class="el-icon-user"></i>个人中心
                </el-dropdown-item>
                <el-dropdown-item divided @click.native="handleLogout">
                  <i class="el-icon-switch-button"></i>退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </el-header>

    <el-container class="main-container" v-if="userInfo.role && !loading">
      <transition :name="lowPerformance ? 'fade' : 'slide-fade'">
        <el-aside
            :width="isMobile ? 'min(280px, 85vw)' : '240px'"
            class="sidebar"
            v-show="!isMobile || showMobileMenu"
            :class="{ 'low-performance': lowPerformance,
                      'bg-1': bgType === 1,
                      'bg-2': bgType === 2
                    }"
        >
          <div class="mobile-menu-header" v-if="isMobile">
            <h3>导航菜单</h3>
            <el-button
                type="text"
                @click="showMobileMenu = false"
                icon="el-icon-close"
                class="close-menu-btn"
            ></el-button>
          </div>

          <div class="user-profile">
            <div class="avatar">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="profile-info">
              <div class="profile-name">{{ userInfo.realName || '用户' }}</div>
              <div class="profile-role">{{ userRoleText }}</div>
            </div>
          </div>

          <el-menu
              :default-active="activeMenu"
              class="menu"
              @select="handleMenuSelect"
              background-color="#2c3e50"
              text-color="#bdc3c7"
              active-text-color="#ffffff"
              router
              :collapse="isMobile && !showMobileMenu"
          >
            <template v-if="userRole === 'student'">
              <el-menu-item index="/dashboard/reservation" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-date menu-icon"></i>
                <span class="menu-text">预约练车</span>
              </el-menu-item>
              <el-menu-item index="/dashboard/my-reservations" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-document menu-icon"></i>
                <span class="menu-text">我的预约</span>
              </el-menu-item>
            </template>

            <template v-else-if="userRole === 'coach'">
              <el-menu-item index="/dashboard/schedule" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-date menu-icon"></i>
                <span class="menu-text">我的排班</span>
              </el-menu-item>
              <el-menu-item index="/dashboard/coach-reservations" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-document menu-icon"></i>
                <span class="menu-text">预约管理</span>
              </el-menu-item>
            </template>

            <template v-else-if="userRole === 'admin'">
              <el-menu-item index="/dashboard/user-management" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-user menu-icon"></i>
                <span class="menu-text">用户管理</span>
              </el-menu-item>
              <el-menu-item index="/dashboard/coach-management" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-s-custom menu-icon"></i>
                <span class="menu-text">教练管理</span>
              </el-menu-item>
              <el-menu-item index="/dashboard/system-settings" class="menu-item" style="opacity: 0.7;">
                <i class="el-icon-setting menu-icon"></i>
                <span class="menu-text">系统设置</span>
              </el-menu-item>
            </template>
          </el-menu>

          <div class="menu-footer" v-if="!isMobile">
            <div class="system-info">
              <i class="el-icon-info"></i>
              <span>当前角色: {{ userRoleText }}</span>
            </div>
          </div>
        </el-aside>
      </transition>

      <transition :name="lowPerformance ? 'fade' : 'fade'">
        <div
            v-if="isMobile && showMobileMenu"
            class="mobile-mask"
            @click="showMobileMenu = false"
        ></div>
      </transition>

      <el-main class="main-content">
        <div class="content-wrapper">
          <transition :name="lowPerformance ? 'fade' : 'page-fade'" mode="out-in">
            <router-view></router-view>
          </transition>
        </div>
      </el-main>
    </el-container>

    <div v-else-if="loading" class="loading-container">
      <el-card class="loading-card" shadow="hover">
        <div class="loading-content">
          <i class="el-icon-loading loading-icon" :class="{ 'low-performance': lowPerformance }"></i>
          <p class="loading-text">加载中...</p>
        </div>
      </el-card>
    </div>

    <div v-else-if="!loading" class="error-container">
      <el-card class="error-card" shadow="hover">
        <div class="error-content">
          <i class="el-icon-error error-icon"></i>
          <h3>用户信息加载失败</h3>
          <p>无法获取用户信息，请重新登录</p>
          <el-button type="primary" @click="$router.push('/login')">
            重新登录
          </el-button>
        </div>
      </el-card>
    </div>
    <!-- 全局背景设置抽屉 -->
    <el-drawer
        title="全局背景设置"
        :visible.sync="globalBgDrawerVisible"
        direction="rtl"
        :size="isMobile ? '85%' : '400px'"
        :wrapper-closable="false"
        :before-close="handleGlobalBgDrawerClose"
        custom-class="global-background-settings-drawer"
    >
      <div class="global-bg-drawer-content" v-if="globalBgSettings">
        <!-- 背景类型选择 -->
        <div class="setting-section">
          <h4>背景类型</h4>
          <el-radio-group v-model="globalBgSettings.type" size="small" @change="handleGlobalBgTypeChange">
            <el-radio-button label="image">图片背景</el-radio-button>
            <el-radio-button label="color">纯色背景</el-radio-button>
            <el-radio-button label="none">无背景</el-radio-button>
          </el-radio-group>
        </div>

        <!-- 图片背景设置 -->
        <div v-if="globalBgSettings.type === 'image'" class="setting-section">
          <h4>背景图片</h4>

          <!-- 预设背景 -->
          <div class="bg-presets">
            <div
                v-for="preset in bgPresets"
                :key="preset.name"
                class="bg-preset-item"
                :style="{ backgroundImage: `url(${preset.url})` }"
                @click="selectGlobalBgPreset(preset)"
                :class="{ selected: globalBgSettings.imageUrl === preset.url }"
            >
              <div class="preset-name">{{ preset.name }}</div>
              <div v-if="globalBgSettings.imageUrl === preset.url" class="selected-indicator">
                <i class="el-icon-check"></i>
              </div>
            </div>
          </div>

          <!-- 自定义背景 -->
          <div class="custom-bg-upload">
            <el-upload
                action="#"
                :show-file-list="false"
                :before-upload="() => false"
                :on-change="handleGlobalBgImageUpload"
                accept="image/*"
            >
<!--              <el-button type="primary" size="small" plain icon="el-icon-upload">
                上传自定义背景
              </el-button>-->
            </el-upload>
<!--            <p class="upload-tip">支持 JPG/PNG 格式，最大 5MB</p>-->
          </div>

          <!-- 模糊度调节 -->
          <div class="setting-section">
            <h4>
              模糊度调节
              <span class="blur-value">{{ globalBgSettings.blurLevel }}px</span>
            </h4>
            <el-slider
                v-model="globalBgSettings.blurLevel"
                :min="0"
                :max="20"
                :step="1"
                show-input
                input-size="small"
            ></el-slider>
          </div>

          <!-- 透明度调节 -->
          <div class="setting-section">
            <h4>
              透明度调节
              <span class="opacity-value">{{ Math.round(globalBgSettings.opacity * 100) }}%</span>
            </h4>
            <el-slider
                v-model="globalBgSettings.opacity"
                :min="0.1"
                :max="1"
                :step="0.05"
                show-input
                input-size="small"
            ></el-slider>
          </div>
        </div>

        <!-- 纯色背景设置 -->
        <div v-if="globalBgSettings.type === 'color'" class="setting-section">
          <h4>背景颜色</h4>
          <div class="color-picker-container">
            <el-color-picker
                v-model="globalBgSettings.backgroundColor"
                show-alpha
                size="small"
            ></el-color-picker>
            <el-input
                v-model="globalBgSettings.backgroundColor"
                size="small"
                placeholder="#FFFFFF"
                style="width: 120px; margin-left: 10px;"
            ></el-input>
          </div>
        </div>

        <!-- 全局设置选项 -->
        <div class="setting-section">
          <h4>设置选项</h4>
          <div class="global-options">
            <el-checkbox
                v-model="globalBgSettings.applyGlobally"
            >
              应用到所有页面
            </el-checkbox>
            <el-tooltip content="开启后，此设置将应用于所有支持背景设置的页面" placement="top">
              <i class="el-icon-info info-icon"></i>
            </el-tooltip>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="setting-actions">
          <el-button
              type="primary"
              @click="applyGlobalBackgroundSettings"
              :loading="applyingGlobalBg"
              style="width: 100%; margin-bottom: 8px;"
          >
            应用设置
          </el-button>
          <div class="action-buttons">
            <el-button
                type="default"
                @click="resetGlobalBackgroundSettings"
                style="flex: 1; margin-right: 4px;"
            >
              恢复默认
            </el-button>
            <el-button
                type="text"
                @click="closeGlobalBackgroundSettings"
                style="flex: 1; margin-left: 4px;"
            >
              取消
            </el-button>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
// 导入事件总线
import { EventBus } from '@/utils/eventBus'
import {
  applySettingsToAllPages,
  bgPresets,
  getGlobalBackgroundSettings,
  saveGlobalBackgroundSettings
} from "@/utils/storage";

export default {
  name: 'DashBoard',
  data() {
    return {
      userInfo: {
        username: '',
        realName: '',
        role: ''
      },
      activeMenu: '',
      showMobileMenu: false,
      isMobile: false,
      lowPerformance: false,
      loading: true,
      refreshing: false, // 全局刷新状态
      messagePosition: 'top-right', // 消息位置
      globalBgDrawerVisible: false,
      globalBgSettings: null,
      applyingGlobalBg: false,
      bgPresets: bgPresets,
      bgType: 1
    }
  },
  computed: {
    isGlobalBgActive() {
      return this.globalBgSettings && this.globalBgSettings.type !== 'none'
    },
    globalBgIcon() {
      if (this.isGlobalBgActive) {
        return 'el-icon-picture-outline'
      } else {
        return 'el-icon-picture'
      }
    },
    userRole() {
      if (!this.userInfo.role) return ''
      return this.userInfo.role.toLowerCase()
    },
    userRoleText() {
      const roleMap = {
        'student': '学员',
        'coach': '教练',
        'admin': '管理员'
      }
      return roleMap[this.userRole] || this.userInfo.role
    }
  },
  async mounted() {
    console.log('Dashboard mounted')

    try {
      await this.loadUserInfo()
      this.setActiveMenu()
      this.checkMobile()
      this.detectLowPerformance()
      await this.loadGlobalBackgroundSettings()

      // 如果是直接访问 /dashboard，根据角色自动跳转
      if (this.$route.path === '/dashboard' || this.$route.path === '/dashboard/') {
        this.redirectToRolePage()
      }
    } catch (error) {
      console.error('Dashboard初始化失败:', error)
      this.$router.push('/login')
    } finally {
      this.loading = false
    }

    window.addEventListener('resize', this.checkMobile)

    // 监听子页面的刷新完成事件
    EventBus.$on('refresh-complete', this.handleRefreshComplete)
    // 监听全局背景变化事件
    EventBus.$on('global-background-changed', this.handleGlobalBackgroundChanged)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    // 移除事件监听
    EventBus.$off('refresh-complete', this.handleRefreshComplete)
    EventBus.$off('global-background-changed', this.handleGlobalBackgroundChanged)
  },
  methods: {
    // 加载全局背景设置
    async loadGlobalBackgroundSettings() {
      try {
        const settings = getGlobalBackgroundSettings()
        if (settings) {
          this.globalBgSettings = settings
        } else {
          // 使用默认设置
          this.globalBgSettings = {
            type: 'image',
            imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64',
            blurLevel: 5,
            opacity: 0.8,
            backgroundColor: '#f0f2f5',
            applyGlobally: true
          }
        }
      } catch (error) {
        console.error('加载全局背景设置失败:', error)
      }
    },
    // 切换全局背景设置抽屉
    toggleGlobalBackgroundSettings() {
      this.globalBgDrawerVisible = !this.globalBgDrawerVisible
      if (this.globalBgDrawerVisible) {
        this.loadGlobalBackgroundSettings()
      }
    },
// 处理全局背景抽屉关闭
    handleGlobalBgDrawerClose(done) {
      this.$confirm('是否保存当前设置？', '提示', {
        confirmButtonText: '保存并关闭',
        cancelButtonText: '放弃更改',
        type: 'warning',
        customClass: this.isMobile ? 'mobile-confirm-dialog' : ''
      }).then(() => {
        this.applyGlobalBackgroundSettings()
        done()
      }).catch(() => {
        // 重新加载原始设置
        this.loadGlobalBackgroundSettings()
        done()
      })
    },
    // 处理全局背景类型变化
    handleGlobalBgTypeChange() {
      // 类型变化时，如果是纯色背景，设置默认颜色
      if (this.globalBgSettings.type === 'color' && !this.globalBgSettings.backgroundColor) {
        this.globalBgSettings.backgroundColor = '#f0f2f5'
      }
    },
    // 选择全局背景预设
    selectGlobalBgPreset(preset) {
      this.globalBgSettings.imageUrl = preset.url
      this.globalBgSettings.customImage = null
    },
    // 上传全局背景图片
    handleGlobalBgImageUpload(file) {
      if (!file) return false

      // 检查文件类型
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!validTypes.includes(file.type)) {
        this.showMessage({
          message: '只支持 JPG, PNG, GIF, WebP 格式的图片',
          type: 'error'
        })
        return false
      }

      // 检查文件大小（5MB）
      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        this.showMessage({
          message: '图片大小不能超过 5MB',
          type: 'error'
        })
        return false
      }

      try {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.globalBgSettings.imageUrl = e.target.result
          this.globalBgSettings.customImage = e.target.result
          this.showMessage({
            message: '自定义背景已设置',
            type: 'success'
          })
        }
        reader.onerror = () => {
          this.showMessage({
            message: '图片上传失败',
            type: 'error'
          })
        }
        reader.readAsDataURL(file.raw)
      } catch (error) {
        console.error('上传图片失败:', error)
        this.showMessage({
          message: '图片上传失败',
          type: 'error'
        })
        return false
      }
    },
    // 应用全局背景设置
    async applyGlobalBackgroundSettings() {
      this.applyingGlobalBg = true
      try {
        // 保存到全局
        const success = saveGlobalBackgroundSettings(this.globalBgSettings)

        if (success) {
          // 如果设置了应用到全局，则应用到所有页面
          if (this.globalBgSettings.applyGlobally) {
            await applySettingsToAllPages(this.globalBgSettings)
          }

          // 发出全局背景变化事件
          EventBus.$emit('global-background-changed', this.globalBgSettings)

          this.showMessage({
            message: this.globalBgSettings.applyGlobally ?
                '全局背景设置已应用到所有页面' :
                '全局背景设置已保存',
            type: 'success'
          })

          this.globalBgDrawerVisible = false
        } else {
          this.showMessage({
            message: '保存失败，请重试',
            type: 'error'
          })
        }
      } catch (error) {
        console.error('应用全局背景设置失败:', error)
        this.showMessage({
          message: '保存失败: ' + error.message,
          type: 'error'
        })
      } finally {
        this.applyingGlobalBg = false
      }
    },
// 重置全局背景设置
    async resetGlobalBackgroundSettings() {
      try {
        this.$confirm('确定要重置所有页面的背景为默认设置吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          customClass: this.isMobile ? 'mobile-confirm-dialog' : ''
        }).then(async () => {
          const defaultSettings = {
            type: 'image',
            imageUrl: bgPresets[0].url,
            blurLevel: 5,
            opacity: 0.8,
            backgroundColor: '#f0f2f5',
            applyGlobally: false
          }

          this.globalBgSettings = defaultSettings
          await this.applyGlobalBackgroundSettings()
        })
      } catch (error) {
        // 用户取消操作
      }
    },
    // 更新所有页面的背景（可选，用于即时更新）
    updateAllPageBackgrounds(settings) {
      // 这里可以添加逻辑来更新当前已加载的页面
      // 例如通过事件总线或Vuex通知所有页面更新背景
      console.log('全局背景已更新:', settings)
    },
    // 关闭全局背景设置
    closeGlobalBackgroundSettings() {
      this.globalBgDrawerVisible = false
    },

    // 处理全局背景变化事件
    handleGlobalBackgroundChanged(settings) {
      console.log('收到全局背景变化事件:', settings)
      // 这里可以添加处理逻辑，如果需要实时更新当前页面的显示
    },
    // 检查是否为移动端
    checkMobile() {
      const width = window.innerWidth
      this.isMobile = width <= 768

      if (width <= 480) {
        this.messagePosition = 'top'
      } else if (width <= 768) {
        this.messagePosition = 'top'
      } else {
        this.messagePosition = 'top-right'
      }
    },

    // 统一消息提示方法
    showMessage(options) {
      const config = {
        duration: 3000,
        showClose: true,
        ...options
      }

      if (this.isMobile) {
        config.position = 'top'
        config.duration = 2000
      } else {
        config.position = this.messagePosition
      }

      this.$message(config)
    },

    // 自动跳转到角色页面
    redirectToRolePage() {
      if (!this.userInfo.role) return

      const roleRoutes = {
        'student': '/dashboard/reservation',
        'coach': '/dashboard/coach-reservations',
        'admin': '/dashboard/user-management'
      }

      const targetRoute = roleRoutes[this.userRole]
      if (targetRoute && this.$route.path !== targetRoute) {
        console.log(`自动跳转到: ${targetRoute}`)
        this.$router.replace(targetRoute)
      }
    },

    // 全局刷新方法
    async handleGlobalRefresh() {
      this.refreshing = true

      try {
        // 发出全局刷新事件，让各个页面组件监听并刷新数据
        EventBus.$emit('global-refresh')

        // 同时重新加载用户信息
        await this.loadUserInfo()

        // 设置超时，确保至少显示1.5秒的刷新动画
        setTimeout(() => {
          if (this.refreshing) {
            this.refreshing = false
            this.showMessage({
              message: '数据刷新完成',
              type: 'success'
            })
          }
        }, 1500)
      } catch (error) {
        console.error('刷新数据失败:', error)
        this.showMessage({
          message: '刷新数据失败',
          type: 'error'
        })
        this.refreshing = false
      }
    },

    // 处理子页面刷新完成事件
    handleRefreshComplete() {
      console.log('收到子页面刷新完成信号')
      // 这里可以添加逻辑来跟踪哪些页面已完成刷新
    },

    // 检测低性能设备
    detectLowPerformance() {
      // 通过多种方式检测低性能设备
      const isLowPerformance =
          // 检测移动设备且屏幕较小（通常低端设备屏幕较小）
          (this.isMobile && window.innerWidth <= 480) ||
          // 检测低内存设备
          (navigator.deviceMemory && navigator.deviceMemory <= 2) ||
          // 检测CPU核心数较少
          (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) ||
          // 检测是否支持WebGL（不支持通常表示低端GPU）
          !this.detectWebGLSupport() ||
          // 检测电池信息（低端设备通常电池容量较小）
          this.detectLowEndBattery();

      this.lowPerformance = isLowPerformance;
      console.log('低性能设备检测结果:', this.lowPerformance);
    },

    // 检测WebGL支持
    detectWebGLSupport() {
      try {
        const canvas = document.createElement('canvas');
        return !!(window.WebGLRenderingContext &&
            (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
      } catch (e) {
        return false;
      }
    },

    // 检测低端电池（如果API可用）
    detectLowEndBattery() {
      if ('getBattery' in navigator) {
        return navigator.getBattery().then(battery => {
          return battery && battery.dischargingTime && battery.dischargingTime < 3600;
        }).catch(() => false);
      }
      return false;
    },

    // 获取角色标签类型
    getRoleTagType(role) {
      const types = {
        'student': 'success',
        'coach': 'warning',
        'admin': 'danger'
      }
      return types[role] || 'info'
    },

    // 加载用户信息
    async loadUserInfo() {
      return new Promise((resolve, reject) => {
        const userInfoStr = localStorage.getItem('userInfo')
        const token = localStorage.getItem('token')

        console.log('从localStorage获取的用户信息:', userInfoStr)
        // console.log('Token:', token)

        if (!token) {
          console.warn('Token不存在，跳转到登录页')
          this.showMessage({
            message: '登录已过期，请重新登录',
            type: 'error'
          })
          this.$router.push('/login')
          reject(new Error('Token不存在'))
          return
        }

        if (userInfoStr) {
          try {
            this.userInfo = JSON.parse(userInfoStr)
            console.log('解析后的用户信息:', this.userInfo)
            console.log('用户角色:', this.userInfo.role)
            console.log('标准化角色:', this.userRole)

            if (!this.userInfo.role) {
              console.warn('用户角色为空，设置为student用于测试')
              this.userInfo.role = 'student'
            }
            resolve(this.userInfo)
          } catch (error) {
            console.error('解析用户信息失败:', error)
            this.showMessage({
              message: '用户信息格式错误，请重新登录',
              type: 'error'
            })
            this.$router.push('/login')
            reject(error)
          }
        } else {
          console.warn('用户信息不存在，尝试从API获取')
          // 这里可以添加从API获取用户信息的逻辑
          this.showMessage({
            message: '用户信息不存在，请重新登录',
            type: 'warning'
          })
          this.$router.push('/login')
          reject(new Error('用户信息不存在'))
        }
      })
    },

    // 设置激活菜单
    setActiveMenu() {
      const currentPath = this.$route.path
      console.log('当前路由路径:', currentPath)
      this.activeMenu = currentPath
      console.log('设置激活菜单:', this.activeMenu)
    },

    // 菜单选择处理
    handleMenuSelect(index) {
      if (this.$route.path !== index) {
        console.log('菜单项被点击:', index)
        this.activeMenu = index
        this.$router.push(index)
        // 移动端选择菜单后自动关闭菜单
        if (this.isMobile) {
          this.showMobileMenu = false
        }
      } else {
        console.log('已经在当前路由:', index)
      }
    },

    // 个人中心
    handleProfile() {
      this.$router.push('/personal-center')
    },

    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: this.isMobile ? 'mobile-confirm-dialog logout-confirm' : 'logout-confirm',
        center: this.isMobile
      }).then(() => {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        this.showMessage({
          message: '退出成功',
          type: 'success'
        })
        this.$router.push('/login')
      }).catch(() => {
        // 用户取消操作
      })
    }
  },
  watch: {
    '$route'(to) {
      console.log('路由变化:', to.path)
      this.setActiveMenu()
    }
  }
}
</script>

<style scoped>

.sidebar.bg-1 {
  background-image: url('@/assets/bg1.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.sidebar.bg-2 {
  background-image: url('@/assets/bg2.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.sidebar.bg-1 .menu,
.sidebar.bg-2 .menu {
  background-color: transparent !important;
}

/* 调整侧边栏内容颜色以适应背景 */
.sidebar.bg-1,
.sidebar.bg-2 {
  color: white;
}


.dashboard-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f5f7fa;
}

/* 头部样式 */
.header {
  background: #154373;
  color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: 64px;
  flex-shrink: 0;
  position: relative;
  z-index: 1001;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-icon {
  font-size: 24px;
  color: white;
}

.logo-text {
  margin: 0;
  color: white;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
}

.mobile-menu-btn {
  color: white;
  font-size: 18px;
  padding: 10px 12px; /* 增大触摸区域 */
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  transition: transform 0.15s ease;
  will-change: transform;
}

.mobile-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.mobile-menu-btn:active {
  transform: scale(0.95);
}

.header-right {
  display: flex;
  align-items: center;
}

.user-area {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  transform: translateZ(0);
}

.user-area:hover {
  background: rgba(255, 255, 255, 0.1);
}

.user-icon {
  font-size: 18px;
  color: white;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
}

.role-tag {
  font-size: 10px;
  height: 18px;
  line-height: 18px;
}

.dropdown-trigger {
  color: white;
  cursor: pointer;
  padding: 8px; /* 增大触摸区域 */
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.dropdown-trigger:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* 主容器 */
.main-container {
  flex: 1;
  display: flex;
  overflow: hidden;
  height: calc(100vh - 64px);
  position: relative;
}

/* 侧边栏样式 */
.sidebar {
  background-color: #2c3e50;
  height: 100%;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); /* 优化动画曲线 */
  z-index: 1000;
  position: relative;
  box-shadow: 1px 0 4px rgba(0, 0, 0, 0.1);
  transform: translateZ(0);
  will-change: transform;
}

/* 低性能设备侧边栏样式 */
.sidebar.low-performance {
  transition: none;
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #34495e;
  color: white;
  border-bottom: 1px solid #3c5066;
}

.mobile-menu-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.close-menu-btn {
  color: white;
  font-size: 16px;
  transition: transform 0.15s ease;
}

.close-menu-btn:hover {
  transform: rotate(90deg);
}

.user-profile {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #3c5066;
  transition: background-color 0.2s ease;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.05);
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #3498db;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  transition: transform 0.2s ease;
  will-change: transform;
}

.avatar:hover {
  transform: scale(1.05);
}

.avatar i {
  font-size: 24px;
  color: white;
}

.profile-info {
  flex: 1;
}

.profile-name {
  font-size: 16px;
  font-weight: 500;
  color: white;
  margin-bottom: 4px;
}

.profile-role {
  font-size: 12px;
  color: #bdc3c7;
}

.menu {
  border: none;
  height: calc(100% - 140px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.menu-item {
  margin: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease, transform 0.15s ease;
  position: relative;
  overflow: hidden;
  transform: translateZ(0);
  will-change: transform;
}

.menu-item:hover {
  background-color: #34495e !important;
  transform: translateX(3px);
}

.menu-item.is-active {
  background-color: #3498db !important;
  transform: translateX(5px);
}

/* 低性能设备菜单项样式 */
.menu-item.low-performance {
  transition: background-color 0.2s ease;
}

.menu-item.low-performance:hover,
.menu-item.low-performance.is-active {
  transform: none;
}

.menu-item.is-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: #ffffff;
  animation: slideInLeft 0.2s ease;
}

.menu-item.low-performance.is-active::before {
  animation: none;
}

.menu-icon {
  font-size: 18px;
  margin-right: 8px;
  color: #bdc3c7;
  transition: color 0.2s ease;
}

.menu-item:hover .menu-icon {
  color: #ffffff;
}

.menu-text {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  transition: color 0.2s ease;
}

.menu-item:hover .menu-text {
  color: #5fc2b5;
}

.menu-footer {
  padding: 16px 20px;
  border-top: 1px solid #3c5066;
}

.system-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #95a5a6;
}

.mobile-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  transform: translateZ(0);
}

/* 主内容区域 */
.main-content {
  padding: 0;
  background-color: #f5f7fa;
  overflow-y: auto;
  height: 100%;
  flex: 1;
  min-width: 0;
  -webkit-overflow-scrolling: touch;
}

.content-wrapper {
  padding: 20px;
  height: 100%;
}

/* 加载状态 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 64px);
  background-color: #f5f7fa;
  padding: 20px;
}

.loading-card {
  width: 200px;
  text-align: center;
  border-radius: 12px;
  border: none;
  animation: pulse 1.5s infinite;
}

.loading-content {
  padding: 40px 20px;
}

.loading-icon {
  font-size: 28px;
  margin-bottom: 12px;
  animation: rotating 1.5s linear infinite;
  color: #409EFF;
}

/* 低性能设备加载图标 */
.loading-icon.low-performance {
  animation: rotating 2s linear infinite;
}

.loading-text {
  color: #606266;
  font-size: 14px;
  margin: 0;
  animation: fadeIn 1s ease-in-out infinite alternate;
}

/* 错误容器 */
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 64px);
  padding: 20px;
}

.error-card {
  width: 400px;
  text-align: center;
  border-radius: 12px;
}

.error-content {
  padding: 40px 20px;
}

.error-icon {
  font-size: 48px;
  color: #F56C6C;
  margin-bottom: 20px;
}

.error-content h3 {
  color: #303133;
  margin-bottom: 12px;
}

.error-content p {
  color: #606266;
  margin-bottom: 20px;
}

/* 全局背景控制按钮 */
.global-bg-control-btn {
  margin-right: 10px;
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border: none !important;
  transition: all 0.3s ease;
}

.global-bg-control-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.1);
}

.global-bg-control-btn:active {
  transform: scale(0.95);
}

/* 全局背景设置抽屉内容 */
.global-bg-drawer-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.setting-section {
  margin-bottom: 24px;
}

.setting-section h4 {
  margin-bottom: 12px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.blur-value, .opacity-value {
  color: #409EFF;
  font-size: 12px;
  font-weight: normal;
}

.bg-presets {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.bg-preset-item {
  height: 80px;
  border-radius: 6px;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.bg-preset-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.bg-preset-item.selected {
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.3);
}

.preset-name {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px;
  font-size: 12px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.selected-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #409EFF;
  color: white;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.custom-bg-upload {
  margin-bottom: 16px;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  text-align: center;
}

.color-picker-container {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.global-options {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-icon {
  color: #909399;
  cursor: help;
}

.setting-actions {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

/* 移动端响应式 */
@media (max-width: 768px) {
  .global-bg-drawer-content {
    padding: 16px;
  }

  .bg-presets {
    grid-template-columns: 1fr;
  }

  .global-bg-control-btn {
    margin-right: 8px;
    padding: 8px !important;
  }
}

/* 平板优化 */
@media (min-width: 769px) and (max-width: 1024px) {
  .bg-presets {
    grid-template-columns: repeat(2, 1fr);
  }
}
/* ========== 关键帧动画 ========== */

/* 旋转动画 */
@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 简化版侧边栏滑入动画 */
@keyframes slideInLeft {
  from {
    transform: translateX(-5px);
    opacity: 0.8;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* 简化版脉冲动画 */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

/* 简化版淡入动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ========== Vue Transition 动画类 ========== */

/* 侧边栏滑入淡出过渡 */
.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(0.9, 0, 0.2, 1);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* 遮罩层淡入淡出过渡 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 页面切换淡入淡出过渡 */
.page-fade-enter-active, .page-fade-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); /* 优化动画曲线 */
}

.page-fade-enter {
  opacity: 0;
  transform: translateY(15px); /* 修改为向上淡入 */
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-15px); /* 修改为向上淡出 */
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-content {
    padding: 0 16px;
  }

  .logo-text {
    display: none; /* 隐藏标题 */
  }

  .user-area {
    padding: 6px 10px;
  }

  .user-name {
    font-size: 13px;
  }

  .main-content {
    padding: 0;
  }

  .content-wrapper {
    padding: 16px;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(-100%);
  }

  .sidebar:not(.el-aside--hidden) {
    transform: translateX(0);
  }

  .main-container {
    height: calc(100vh - 64px);
  }

  /* 移动端进一步减少动画效果 */
  .menu-item {
    margin: 8px 12px; /* 增加移动端菜单间距 */
  }

  .menu-item:hover {
    transform: translateX(2px);
  }

  /* 低端移动设备完全禁用复杂动画 */
  @media (max-width: 480px) {
    .menu-item {
      transition: background-color 0.2s ease;
    }

    .menu-item:hover,
    .menu-item.is-active {
      transform: none;
    }

    .avatar:hover {
      transform: none;
    }
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0 12px;
  }

  .logo-text {
    font-size: 15px;
    white-space: normal; /* 允许Logo文字换行 */
    line-height: 1.3;
    max-width: 150px; /* 限制最大宽度以触发换行 */
  }

  .content-wrapper {
    padding: 12px;
  }
}

/* 极窄屏幕优化 */
@media (max-width: 360px) {
  .logo-text {
    display: none; /* 在非常窄的屏幕上隐藏Logo文字 */
  }

  .header-left {
    gap: 8px; /* 缩小图标和菜单按钮的间距 */
  }
}


/* 平板优化 */
@media (min-width: 769px) and (max-width: 1024px) {
  .sidebar {
    width: 220px !important;
  }

  .logo-text {
    font-size: 16px;
  }
}

/* 滚动条优化 */
.menu::-webkit-scrollbar {
  width: 4px;
}

.menu::-webkit-scrollbar-track {
  background: #263445;
}

.menu::-webkit-scrollbar-thumb {
  background: #4a5f7a;
  border-radius: 2px;
  transition: background-color 0.2s ease;
}

.menu::-webkit-scrollbar-thumb:hover {
  background: #5a718a;
}

/* 自定义确认框样式 */
.logout-confirm .el-message-box__header {
  background: #409EFF;
}

.logout-confirm .el-message-box__title {
  color: white;
}

.logout-confirm .el-message-box__headerbtn .el-message-box__close {
  color: white;
}

/* 针对低性能设备的特殊优化 */
@media (max-width: 480px), (pointer: coarse) and (max-width: 1024px) {
  /* 减少所有动画的持续时间和复杂度 */
  * {
    animation-duration: 0.3s !important;
    transition-duration: 0.2s !important;
  }
}

/* 强制启用GPU加速的元素 */
.gpu-accelerated {
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
}

/* 减少重绘的区域 */
.reduce-repaint {
  will-change: transform, opacity;
}

/* 低性能设备通用类 */
.low-performance {
  animation: none !important;
  transition: none !important;
}

.low-performance:hover {
  transform: none !important;
}
</style>

<style>
/* 移动端确认对话框样式 */
.mobile-confirm-dialog {
  width: 90% !important;
  max-width: 320px !important;
  margin: 0 auto !important;
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
}

.mobile-confirm-dialog .el-message-box__header {
  padding: 16px 20px 10px;
}

.mobile-confirm-dialog .el-message-box__title {
  font-size: 18px;
  text-align: center;
  width: 100%;
}

.mobile-confirm-dialog .el-message-box__content {
  padding: 20px;
}

.mobile-confirm-dialog .el-message-box__message {
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
}

.mobile-confirm-dialog .el-message-box__btns {
  padding: 0 20px 20px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.mobile-confirm-dialog .el-message-box__btns .el-button {
  flex: 1;
  min-width: 0;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

/* 移动端消息提示优化 */
@media (max-width: 768px) {
  /* 移动端消息提示样式优化 */
  .el-message {
    width: 90% !important;
    min-width: auto !important;
    max-width: 320px !important;
    margin: 0 auto !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
  }

  .el-message--top {
    top: 20px !important;
  }

  .el-message__content {
    font-size: 15px !important;
    line-height: 1.4 !important;
    text-align: center !important;
  }

  .el-message .el-icon-close {
    font-size: 16px !important;
    right: 12px !important;
    top: 12px !important;
  }
}

/* 超小屏幕消息提示优化 */
@media (max-width: 480px) {
  .el-message {
    width: 95% !important;
    max-width: 280px !important;
    padding: 14px 16px !important;
  }

  .el-message__content {
    font-size: 14px !important;
    padding-right: 24px !important;
  }

  .el-message .el-icon-close {
    right: 10px !important;
    top: 10px !important;
  }

  .el-message--top {
    top: 15px !important;
  }
}

/* 极窄屏幕调整 */
@media (max-width: 360px) {
  .el-message {
    width: 98% !important;
    max-width: 260px !important;
    padding: 12px 14px !important;
  }

  .el-message__content {
    font-size: 13px !important;
    padding-right: 22px !important;
  }
}
</style>
